/**
 * BatchEnrichmentPanel Component
 *
 * Shows enrichment status and allows triggering batch AI enrichment
 * for products missing enrichment. Used after wizard completion when
 * enrichment was skipped to reduce onboarding time.
 *
 * Created: 2026-02-21
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sparkles,
  DollarSign,
  Clock,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Zap
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';

interface CostBreakdown {
  inputTokens: number;
  outputTokens: number;
  inputCost: number;
  outputCost: number;
}

interface CostEstimate {
  amount: number;
  currency: string;
  model: string;
  provider: string;
  breakdown: CostBreakdown;
}

interface LatestBatch {
  id: string;
  status: string;
  total_products: number;
  processed_products: number;
  succeeded_products: number;
  created_at: string;
  started_at: string | null;
  progress_percent: number;
}

interface EnrichmentStatus {
  totalProducts: number;
  enrichedProducts: number;
  productsNeedingEnrichment: number;
  percentEnriched: number;
  hasActiveBatches: boolean;
  latestBatch: LatestBatch | null;
  costEstimate: CostEstimate;
  estimatedDuration: string;
  canStartEnrichment: boolean;
}

interface BatchEnrichmentPanelProps {
  siteId: string;
  onRefresh?: () => void;
}

export function BatchEnrichmentPanel({ siteId, onRefresh }: BatchEnrichmentPanelProps) {
  const [status, setStatus] = useState<EnrichmentStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    try {
      setError(null);
      const response = await authFetch(`/api/aeo-readiness/site/${siteId}/enrichment-estimate`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load enrichment status');
      }

      const result = await response.json();
      setStatus(result.data);
    } catch (err) {
      console.error('Failed to load enrichment status:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Poll for updates if there's an active batch
  useEffect(() => {
    if (!status?.hasActiveBatches) return;

    const interval = setInterval(() => {
      loadStatus();
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(interval);
  }, [status?.hasActiveBatches, loadStatus]);

  const handleStartEnrichment = async () => {
    setIsStarting(true);
    setStartError(null);

    try {
      const response = await authFetch(`/api/aeo-readiness/site/${siteId}/start-enrichment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userConfirmedCost: true })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start enrichment');
      }

      // Success - close dialog and refresh
      setShowConfirmDialog(false);
      loadStatus();
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      console.error('Failed to start enrichment:', err);
      setStartError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsStarting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-muted-foreground">
            <RefreshCw className="h-4 w-4 animate-spin" />
            Loading enrichment status...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200">
        <CardContent className="pt-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button onClick={loadStatus} variant="outline" size="sm" className="mt-4">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!status) return null;

  // All products enriched
  if (status.percentEnriched === 100) {
    return (
      <Card className="border-green-200 bg-green-50 dark:bg-green-950/20">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-400">
            <CheckCircle2 className="h-5 w-5" />
            All Products Enriched
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-green-600 dark:text-green-500">
            All {status.totalProducts.toLocaleString()} products have been AI enriched with attributes.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Active batch in progress
  if (status.hasActiveBatches && status.latestBatch) {
    const batch = status.latestBatch;
    const progress = batch.progress_percent || 0;

    return (
      <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950/20">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-blue-700 dark:text-blue-400">
            <RefreshCw className="h-5 w-5 animate-spin" />
            Enrichment in Progress
          </CardTitle>
          <CardDescription className="text-blue-600">
            AI enrichment batch is processing your products
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-blue-700 dark:text-blue-400">Progress</span>
              <span className="text-blue-600">
                {batch.processed_products?.toLocaleString() || 0} / {batch.total_products?.toLocaleString() || 0} products
              </span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Status</span>
              <div className="font-medium capitalize">{batch.status}</div>
            </div>
            <div>
              <span className="text-muted-foreground">Succeeded</span>
              <div className="font-medium text-green-600">{batch.succeeded_products || 0}</div>
            </div>
            <div>
              <span className="text-muted-foreground">Started</span>
              <div className="font-medium">
                {batch.started_at
                  ? new Date(batch.started_at).toLocaleTimeString()
                  : 'Pending'}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Products need enrichment - show trigger panel
  return (
    <>
      <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
            <Sparkles className="h-5 w-5" />
            AI Product Enrichment Available
          </CardTitle>
          <CardDescription className="text-amber-600">
            {status.productsNeedingEnrichment.toLocaleString()} products need AI attribute extraction
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Progress bar */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-muted-foreground">Current enrichment</span>
              <span className="font-medium">
                {status.enrichedProducts.toLocaleString()} / {status.totalProducts.toLocaleString()} ({status.percentEnriched}%)
              </span>
            </div>
            <Progress value={status.percentEnriched} className="h-2" />
          </div>

          {/* Cost estimate */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-start gap-2">
              <DollarSign className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div>
                <div className="text-xs text-muted-foreground">Estimated Cost</div>
                <div className="font-semibold">
                  ${status.costEstimate.amount.toFixed(2)}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Clock className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div>
                <div className="text-xs text-muted-foreground">Duration</div>
                <div className="font-semibold">{status.estimatedDuration}</div>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Zap className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div>
                <div className="text-xs text-muted-foreground">Model</div>
                <div className="font-semibold text-sm">
                  {status.costEstimate.model}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Sparkles className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div>
                <div className="text-xs text-muted-foreground">Products</div>
                <div className="font-semibold">
                  {status.productsNeedingEnrichment.toLocaleString()}
                </div>
              </div>
            </div>
          </div>

          {/* Provider badge */}
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs">
              {status.costEstimate.provider === 'vertexai' ? 'Google Vertex AI' : 'OpenAI'}
            </Badge>
            <span className="text-xs text-muted-foreground">
              50% cheaper than standard API rates
            </span>
          </div>

          {/* Start button */}
          <Button
            onClick={() => setShowConfirmDialog(true)}
            className="w-full"
            disabled={!status.canStartEnrichment}
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Start AI Enrichment
          </Button>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start AI Product Enrichment?</DialogTitle>
            <DialogDescription>
              This will process {status.productsNeedingEnrichment.toLocaleString()} products using {status.costEstimate.model}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="p-3 bg-muted rounded-lg">
                <div className="text-muted-foreground">Estimated Cost</div>
                <div className="text-2xl font-bold">${status.costEstimate.amount.toFixed(2)}</div>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <div className="text-muted-foreground">Estimated Time</div>
                <div className="text-2xl font-bold">{status.estimatedDuration}</div>
              </div>
            </div>

            <div className="text-sm text-muted-foreground">
              <ul className="list-disc pl-4 space-y-1">
                <li>Processing runs in the background - you can close this page</li>
                <li>Progress will be shown in the AEO Readiness Dashboard</li>
                <li>You'll be notified when enrichment completes</li>
              </ul>
            </div>

            {startError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{startError}</AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)} disabled={isStarting}>
              Cancel
            </Button>
            <Button onClick={handleStartEnrichment} disabled={isStarting}>
              {isStarting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Confirm & Start
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
